/**
 * Offline cache coherence for multi-instance Large UPS (#996 Phase 2).
 * Keeps request.largeUpsConfigurations aligned with the large UPS API / React Query.
 * Mirrors smallUpsOfflineSync.
 */

import { offlineCache, type OfflineLargeUpsOperation } from './offlineCache';
import { applyPendingRepeatableOperations } from './repeatableSectionOfflineSync';
import { groupLargeUpsByStatus } from '../components/largeups/largeUpsListUtils';

import type {
  GroupedLargeUpsConfigurations,
  LargeUpsConfiguration,
} from '../types/largeUps';

/** Cooling system list from cached request detail (offline reads). */
export async function getCachedLargeUpsList(
  requestId: string,
): Promise<LargeUpsConfiguration[]> {
  const cachedRequest = await offlineCache.getCachedRequestById(requestId);
  return cachedRequest?.largeUpsConfigurations ?? [];
}

/** Grouped units derived from cached list (same shape as GET …/large-ups/grouped). */
export async function getCachedLargeUpsGrouped(
  requestId: string,
): Promise<GroupedLargeUpsConfigurations> {
  return groupLargeUpsByStatus(await getCachedLargeUpsList(requestId));
}

/** Single unit from cached list when offline. */
export async function getCachedLargeUpsById(
  requestId: string,
  largeUpsId: number,
): Promise<LargeUpsConfiguration | undefined> {
  return (await getCachedLargeUpsList(requestId)).find(
    unit => unit.id === largeUpsId,
  );
}

/**
 * After a successful online large UPS fetch or mutation refetch, mirror units onto
 * the cached request so offline reads stay consistent.
 */
export async function mirrorLargeUpsToOfflineRequestCache(
  requestId: string,
  largeUps: LargeUpsConfiguration[],
): Promise<void> {
  const cachedRequest = await offlineCache.getCachedRequestById(requestId);
  if (!cachedRequest) {
    return;
  }
  await offlineCache.updateCachedRequestLargeUps(requestId, largeUps);
}

/** Pending offline large UPS queue entries for one request (create/update/delete/reorder). */
export async function getPendingLargeUpsOpsForRequest(
  requestId: string,
): Promise<
  Awaited<ReturnType<typeof offlineCache.getPendingLargeUpsOperations>>
> {
  const pending = await offlineCache.getPendingLargeUpsOperations();
  return pending.filter(op => String(op.requestId) === String(requestId));
}

/**
 * In-flight offline large UPS ops (status `pending` OR `syncing`) for one request.
 * Used by the ONLINE overlay: the sync flips an op to `syncing` BEFORE its POST,
 * so a `pending`-only view drops an op mid-sync and the item vanishes during the
 * reconnect race. Including `syncing` keeps it visible until the op is dequeued.
 */
export async function getInFlightLargeUpsOpsForRequest(
  requestId: string,
): Promise<OfflineLargeUpsOperation[]> {
  const queue = await offlineCache.getOfflineLargeUpsQueue();
  return queue.filter(
    op =>
      (op.status === 'pending' || op.status === 'syncing') &&
      String(op.requestId) === String(requestId),
  );
}

/**
 * Overlay pending offline large UPS operations onto a base list so offline reads
 * reflect not-yet-synced changes. Pure and deterministic (ops applied in
 * timestamp order). De-dupes creates by id so an optimistic unit already written
 * into the base list is not duplicated.
 */
export function applyPendingLargeUpsOperations(
  requestId: string,
  base: LargeUpsConfiguration[],
  ops: OfflineLargeUpsOperation[],
): LargeUpsConfiguration[] {
  return applyPendingRepeatableOperations(requestId, base, ops, {
    instanceId: op => op.largeUpsId,
    reorderIds: op => op.largeUpsIds,
  });
}

/** Cached large UPS list with pending offline operations overlaid (offline reads). */
export async function getOfflineLargeUpsList(
  requestId: string,
): Promise<LargeUpsConfiguration[]> {
  const [base, ops] = await Promise.all([
    getCachedLargeUpsList(requestId),
    getPendingLargeUpsOpsForRequest(requestId),
  ]);
  return applyPendingLargeUpsOperations(requestId, base, ops);
}

/** Grouped units (existing/new) from the overlaid offline list. */
export async function getOfflineLargeUpsGrouped(
  requestId: string,
): Promise<GroupedLargeUpsConfigurations> {
  return groupLargeUpsByStatus(await getOfflineLargeUpsList(requestId));
}

/** Single unit from the overlaid offline list (includes pending creates). */
export async function getOfflineLargeUpsById(
  requestId: string,
  largeUpsId: number,
): Promise<LargeUpsConfiguration | undefined> {
  return (await getOfflineLargeUpsList(requestId)).find(
    unit => unit.id === largeUpsId,
  );
}
